<script setup>
  const props = defineProps({
    user: {
      type: Object,
      default: () => ({})
    }
  })

  const { getPictureUrl } = useMember()
  const localePath = useLocalePath()
  const menu = ref()
  const open = ref(false)
  const toggle = (event) => {
    open.value = !open.value
    if (menu.value) {
      menu.value.toggle(event)
    }
  }

  // Get authData - these are computed refs that will update when store changes
  const { isIndividuel, isCollectif, isFirme, barData, firmData, userData } = useAuthData()
  const { isPresident } = useIsPresident()
  const { hasUnpaidContribution } = useContributionStatus()

  const userLocation = computed(() => {
    // For individuel users, use userData
    if (
      isIndividuel.value &&
      userData.value?.directory_address?.city?.name &&
      userData.value?.directory_address?.country?.name
    ) {
      return `${userData.value.directory_address.city.name}, ${userData.value.directory_address.country.name}`
    }
    return ''
  })

  const userMemberSince = computed(() => {
    if (props.user?.createdAt) {
      const date = new Date(props.user.createdAt)
      return date.getFullYear()
    }
    return ''
  })

  const displayName = computed(() => {
    if (isIndividuel.value && userData.value) {
      return `${userData.value.lastname || ''} ${userData.value.firstname || ''}`.trim()
    }
    if (isCollectif.value && barData.value) {
      return barData.value.name || ''
    }
    if (isFirme.value && firmData.value) {
      return firmData.value.name || ''
    }
    return props.user?.email || ''
  })

  const displayCode = computed(() => {
    if (isIndividuel.value) {
      return userData.value?.user_code || ''
    }
    return '' // Bars and firms don't have user codes
  })

  const displayPhoto = computed(() => {
    if (isIndividuel.value) {
      return userData.value?.photo
    }
    if (isFirme.value && firmData.value?.logo) {
      return firmData.value.logo
    }
    return null
  })
</script>
<template>
  <Button
    type="button"
    variant="link"
    aria-haspopup="true"
    aria-controls="account_menu"
    pt:root="!text-xs !text-white hover:!text-sky-100 !flex !items-center !gap-2 !h-8 !py-0"
    @click="toggle"
  >
    <template #default>
      <div class="flex items-center gap-2">
        <div class="w-[22px] h-[22px] bg-white/20 rounded-full items-center justify-center flex">
          <font-awesome class="text-xs" :icon="['fas', 'user']" />
        </div>
        {{ $t('pages.auth.menu.myAccount') }}
        <font-awesome
          class="text-xs fa-fw transition-transform duration-200"
          :class="{ 'rotate-180': open }"
          :icon="['fas', 'chevron-down']"
        />
      </div>
    </template>
  </Button>
  <Popover
    ref="menu"
    pt:root="!z-[1200]"
    pt:content="!p-0 !min-w-[320px] !bg-white !shadow-xl !border !border-slate-100"
  >
    <div class="bg-white overflow-hidden">
      <!-- User Header -->
      <div class="px-4 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
        <div class="flex items-center gap-4">
          <img
            v-if="displayPhoto"
            class="w-14 h-14 rounded-md shadow object-cover bg-sky-600 flex-shrink-0"
            :src="getPictureUrl(displayPhoto)"
          />
          <div v-else class="w-14 h-14 bg-sky-600 rounded-md shadow flex-shrink-0 flex items-center justify-center">
            <font-awesome class="text-white text-lg" :icon="['fas', isIndividuel ? 'user' : 'building']" />
          </div>
          <div class="flex-1 min-w-0 space-y-1">
            <h3 class="font-semibold text-slate-900 text-sm truncate">
              {{ displayName }}
            </h3>
            <p v-if="displayCode" class="text-slate-600 text-xs truncate">{{ displayCode }}</p>
            <p v-if="userLocation" class="text-slate-500 text-xs truncate">
              <font-awesome class="fa-fw mr-1" :icon="['fas', 'map-marker-alt']" />
              {{ userLocation }}
            </p>
          </div>
        </div>
      </div>

      <!-- User Information -->
      <div class="px-4 py-4 space-y-3 border-b border-slate-200">
        <div class="grid grid-cols-1 gap-2 text-xs">
          <div class="flex justify-between items-center">
            <span class="text-slate-600 font-medium"
              >{{ $t('pages.auth.menu.email') }}{{ $t('global.punctuation.colon') }}</span
            >
            <span class="text-slate-900 truncate ml-2">{{ user.email }}</span>
          </div>
          <div v-if="user.language" class="flex justify-between items-center">
            <span class="text-slate-600 font-medium"
              >{{ $t('pages.auth.menu.language') }}{{ $t('global.punctuation.colon') }}</span
            >
            <span class="text-slate-900 capitalize">{{ $t(`global.languages.${user.language}`) }}</span>
          </div>
        </div>
      </div>

      <!-- Contribution warning -->
      <div v-if="hasUnpaidContribution" class="px-4 py-3 bg-amber-50 border-b border-amber-200">
        <NuxtLink
          :to="localePath({ name: 'auth-private-profile' })"
          class="flex items-start gap-2 text-xs text-amber-800 hover:text-amber-900 transition-colors"
        >
          <font-awesome :icon="['fas', 'exclamation-triangle']" class="text-amber-600 mt-0.5 flex-shrink-0" />
          <span>{{ $t('pages.auth.contribution.warningTitle') }}</span>
        </NuxtLink>
      </div>

      <!-- Actions -->
      <div class="px-1 py-4">
        <div class="space-y-2">
          <NuxtLink
            :to="localePath({ name: 'auth-private' })"
            class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
          >
            <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'tachometer-alt']" />
            {{ $t('pages.auth.private.dashboard.title') }}
          </NuxtLink>

          <!-- Individuel user menu items -->
          <template v-if="isIndividuel">
            <NuxtLink
              :to="localePath({ name: 'auth-private-profile' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'user']" />
              {{ $t('pages.auth.private.profile.title') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath({ name: 'auth-private-my-documents' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'file-contract']" />
              {{ $t('pages.auth.private.myDocuments.title') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath({ name: 'auth-private-events' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'calendar']" />
              {{ $t('pages.auth.private.events.title') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath({ name: 'auth-private-commissions' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'users']" />
              {{ $t('pages.auth.private.commissions.title') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath({ name: 'auth-private-committees' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'user-friends']" />
              {{ $t('pages.auth.private.committees.title') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath({ name: 'auth-private-national-committees' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'globe']" />
              {{ $t('pages.auth.private.nationalCommittees.title') }}
            </NuxtLink>
            <!-- <NuxtLink
              :to="localePath({ name: 'auth-private-mandates' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'scroll']" />
              {{ $t('pages.auth.private.mandates.title') }}
            </NuxtLink> -->
            <NuxtLink
              :to="localePath({ name: 'auth-private-mandates' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'scroll']" />
              {{ $t('pages.auth.private.mandates.title') }}
            </NuxtLink>
            <NuxtLink
              v-if="isPresident"
              :to="localePath({ name: 'auth-private-president-communication' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'envelope']" />
              {{ $t('pages.auth.private.presidentCommunication.title') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath({ name: 'auth-private-transactions' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'file-invoice']" />
              {{ $t('pages.auth.private.transactions.title') }}
            </NuxtLink>
          </template>

          <!-- Collectif (Bar) user menu items -->
          <template v-if="isCollectif">
            <NuxtLink
              :to="localePath({ name: 'auth-private-bar-profile' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'building']" />
              {{ $t('pages.auth.private.barProfile.title') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath({ name: 'auth-private-bar-members' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'users']" />
              {{ $t('pages.auth.private.barMembers.title') }}
            </NuxtLink>
            <NuxtLink
              v-if="isPresident"
              :to="localePath({ name: 'auth-private-president-communication' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'envelope']" />
              {{ $t('pages.auth.private.presidentCommunication.title') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath({ name: 'auth-private-transactions' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'file-invoice']" />
              {{ $t('pages.auth.private.transactions.title') }}
            </NuxtLink>
          </template>

          <!-- Firme user menu items -->
          <template v-if="isFirme">
            <NuxtLink
              :to="localePath({ name: 'auth-private-firm-profile' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'building']" />
              {{ $t('pages.auth.private.firmProfile.title') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath({ name: 'auth-private-firm-members' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'users']" />
              {{ $t('pages.auth.private.firmMembers.title') }}
            </NuxtLink>
            <NuxtLink
              v-if="isPresident"
              :to="localePath({ name: 'auth-private-president-communication' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'envelope']" />
              {{ $t('pages.auth.private.presidentCommunication.title') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath({ name: 'auth-private-transactions' })"
              class="w-full flex gap-2 items-center px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-slate-900 hover:transform rounded-md transition-all duration-200 cursor-pointer"
            >
              <font-awesome class="fa-fw text-slate-500" :icon="['fas', 'file-invoice']" />
              {{ $t('pages.auth.private.transactions.title') }}
            </NuxtLink>
          </template>

          <hr class="my-3 border-slate-200" />

          <slot name="menu"></slot>
        </div>
      </div>
    </div>
  </Popover>
</template>
